import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { IntegrationApi } from '@alliander-fe/api';

import {
  Heading,
  Text,
  Stack,
  Grid,
  Box,
  GridItem,
  DictionaryDialog,
  RichText,
  SubpageHeroSitecore,
  PageGrid,
  ScrollLinkWrapper,
  PostalCodeCheckForm,
  CTABlock,
} from '@ads-core/components';
import { useTrackingContext, useTrackingFormTouch } from '@liander/context';
import { mq } from '@ads-core/breakpoints';
import { useMedia } from '@ads-core/hooks';
import { useAdsContext } from '@ads-core/providers';
import { scrollToElement } from '@ads-core/utils';
import {
  houseNumberAdditionPattern,
  houseNumberPattern,
  postalCodePattern,
  requiredFieldPattern,
} from '@alliander-fe/validation';

import { useTrackingCustomWizardFormFieldError } from 'src/context/Tracking/Wizard';

import { FilteredCongestionCheckerParticulierProps } from 'components/CongestionCheckerParticulier';

import { baseQueryParams, congestionLevel, formatApiPostalCode, formatPostalCode } from 'src/utils';

import { SubmitHandler, useForm } from 'react-hook-form';

import { useParams } from 'src/hooks/useParams';

import { mappedResponseV1 } from './utils/mappedResponseV1';
import { mappedResponseV2 } from './utils/mappedResponseV2';
import { mappedErrorResponse } from './utils/mappedErrorResponse';
import { useWizardTracking } from './hooks/useWizardTracking';
import { WIZARD_NAME } from './constant/constant';
import { Conclusion } from './_Conclusion';
import { CTAImageContainer } from './_CTAImageContianer';

type Address = {
  postalCode: string;
  houseNumber: string;
  houseNumberAddition?: string;
};

const connectionOrigin = {
  closest: 'DichtstbijzijndeLSVerbinding',
  existing: 'BestaandeLSVerbinding',
} as const;

/**
 * @description This component is used to display the congestion checker. We now have two versions of the component.
 * The V1 is the old version and the V2 is the new version.
 * The V1 will be removed in the future.
 *
 * @param props The props for the congestion checker.
 *
 * @returns The congestion checker.
 *
 */
export const CongestionCheckerParticulier = (props: FilteredCongestionCheckerParticulierProps) => {
  const useApiEndpointVersion = props?.useApiEndpointVersionTwo ? 'v2' : 'v1';

  const { trackCustomWizard } = useTrackingContext();
  const router = useRouter();
  const [toggleMessage, setToggleMessage] = React.useState(false);
  const hasExplanation = Boolean(props?.explanation);
  const notificationRef = React.useRef<HTMLDivElement>(null);
  const additionalInfoRef = React.useRef<HTMLDivElement>(null);
  const adsContext = useAdsContext();
  const isDesktop = useMedia(mq.md);

  const { postalCode, houseNumber, houseNumberAddition, city, networkCheck } = useParams();

  const replaceText = useMemo(() => {
    return {
      '{postalCode}': formatPostalCode(postalCode) ?? '',
      '{houseNumber}': houseNumber ?? '',
      '{houseNumberAddition}': houseNumberAddition ?? '',
      '{places}': city ?? '',
    };
  }, [city, houseNumber, houseNumberAddition, postalCode]);

  const { formState, handleSubmit, register } = useForm({
    mode: 'onTouched',
    defaultValues: {
      postalCode: formatApiPostalCode(postalCode) ?? '',
      houseNumber: houseNumber,
      houseNumberAddition: houseNumberAddition,
    },
  });

  const query = useQuery({
    enabled: Boolean(postalCode) && Boolean(houseNumber),
    queryKey: [postalCode, houseNumber, houseNumberAddition],
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () =>
      IntegrationApi.congestionEndpointsGetSmallScaleCongestions({
        PostalCode: formatApiPostalCode(postalCode) ?? '',
        HouseNumber: Number(houseNumber),
        Addition: houseNumberAddition ?? null,
        Version: useApiEndpointVersion,
      }),
    select: ({ errorCode, level, origin }) => {
      const errorResponse = errorCode
        ? mappedErrorResponse({
            code: errorCode,
            ...props,
          })
        : undefined;

      if (errorResponse) {
        return {
          ...errorResponse,
          response: errorCode,
        };
      }

      if (useApiEndpointVersion === 'v2') {
        return {
          ...mappedResponseV2({
            level: level,
            disclaimer: origin === connectionOrigin.closest,
            replaceText,
            ...props,
          }),
          response: level,
        };
      }

      const mappedLevel = congestionLevel(level);

      return {
        ...mappedResponseV1({
          level: mappedLevel,
          disclaimer: origin === connectionOrigin.closest,
          replaceText,
          ...props,
        }),
        response: mappedLevel,
      };
    },
  });

  const { fetchStatus, data, isError } = query;

  const formErrors = Object.values(formState.errors)
    .map((error) => error?.message)
    .filter((message): message is string => message !== undefined);

  useTrackingCustomWizardFormFieldError({
    fieldsErrors: formErrors,
    actionDetail: `${WIZARD_NAME} foutief ingevuld`,
    wizardName: WIZARD_NAME,
    wizardStep: 1,
  });

  useTrackingFormTouch({
    wizardName: WIZARD_NAME,
    wizardStep: 1,
    fields: formState.touchedFields,
  });

  useWizardTracking({ fetchStatus, data, isError });

  const congestionData = useMemo(() => {
    return isError
      ? {
          ...mappedErrorResponse({
            code: '',
            ...props,
          }),
        }
      : data;
  }, [isError, props, data]);

  const onSubmit: SubmitHandler<Address> = (data) => {
    const {
      postalCode: submittedPostalCode,
      houseNumber: submittedHouseNumber,
      houseNumberAddition: submittedHouseNumberAddition,
    } = data;

    if (
      postalCode === submittedPostalCode &&
      houseNumber === submittedHouseNumber &&
      houseNumberAddition === submittedHouseNumberAddition
    ) {
      setToggleMessage(true);
      return;
    }

    const params = new URLSearchParams();
    params.set(baseQueryParams.postalCode, submittedPostalCode.toUpperCase());
    params.set(baseQueryParams.houseNumber, submittedHouseNumber);

    if (submittedHouseNumberAddition) {
      params.set(baseQueryParams.houseNumberAddition, submittedHouseNumberAddition);
    }

    const parsedParams = decodeURIComponent(params.toString());
    const pathname = router.asPath.split('?')[0];

    router.push(
      {
        pathname: pathname,
        search: parsedParams,
      },
      undefined,
      { scroll: false }
    );
  };

  useEffect(() => {
    const hasCongestionData = Boolean(
      congestionData?.feedbackTitle && congestionData?.feedbackText
    );
    if (!isDesktop && hasCongestionData) {
      scrollToElement(notificationRef, adsContext.topHeaderHeight + 32);
    }

    if (isDesktop && hasCongestionData && Boolean(networkCheck)) {
      scrollToElement(additionalInfoRef, adsContext.topHeaderHeight + 32);
    }

    if (congestionData) {
      setToggleMessage(true);
    }
  }, [
    adsContext.topHeaderHeight,
    notificationRef,
    congestionData,
    isDesktop,
    additionalInfoRef,
    networkCheck,
  ]);

  return (
    <Stack gap={{ initial: 12, md: 30 }}>
      <ScrollLinkWrapper anchorId="schaarste-checker">
        <Stack gap={{ initial: 0, md: 16 }}>
          <SubpageHeroSitecore
            text={
              props?.title ? (
                <Heading
                  size="h1"
                  align={{
                    initial: 'start',
                    md: hasExplanation ? 'start' : 'center',
                  }}
                >
                  <DictionaryDialog>{props.title.toString()}</DictionaryDialog>
                </Heading>
              ) : undefined
            }
            explanation={
              props?.explanation ? (
                <Stack alignY="end">
                  <Text as="div">
                    <RichText>{props?.explanation}</RichText>
                  </Text>
                </Stack>
              ) : undefined
            }
          />
          <PageGrid>
            <GridItem columnEnd="-1" columnStart="1">
              <CTABlock>
                <CTABlock.Column
                  columnStart="1"
                  columnEnd={{ initial: '-1', md: '7' }}
                  variant="main"
                  bg="containerPrimary"
                >
                  <Stack gap={4}>
                    {props?.inputFormTitle ? (
                      <Heading size="h3" as="h2" color="onDark">
                        {props?.inputFormTitle}
                      </Heading>
                    ) : null}
                    {props?.inputFormText ? (
                      <Text color="onDark">{props?.inputFormText}</Text>
                    ) : null}
                  </Stack>
                  <Box paddingTop={{ initial: 6, md: 10 }}>
                    <PostalCodeCheckForm color="none" handleOnSubmit={handleSubmit(onSubmit)}>
                      <Grid columns={12} gap={4} rowGap={4}>
                        <GridItem columnStart="1" columnEnd={{ initial: '-1', lg: '7' }} asChild>
                          <Stack isFullWidth>
                            <PostalCodeCheckForm.Input
                              label={props?.postcode?.toString() ?? 'Postcode'}
                              tone="onDark"
                              placeholder="1234AB"
                              error={formState?.errors?.postalCode?.message as string | undefined}
                              {...register('postalCode', {
                                required: requiredFieldPattern('Postcode'),
                                pattern: postalCodePattern,
                              })}
                              autoComplete="postal-code"
                            />
                          </Stack>
                        </GridItem>
                        <GridItem columnStart={{ initial: '1', lg: '7' }} columnEnd="-1">
                          <Stack isFullWidth asChild>
                            <PostalCodeCheckForm.Addendum
                              label={props?.huisnr?.toString() ?? 'Huisnr. en toevoeging'}
                              error={formState?.errors?.houseNumber?.message as string | undefined}
                              tone="onDark"
                              houseNumber={{
                                label: 'Huisnummer',
                                placeholder: '10',
                                error: formState?.errors?.houseNumber?.message as
                                  | string
                                  | undefined,
                                ...register('houseNumber', {
                                  required: requiredFieldPattern('Huisnummer'),
                                  pattern: houseNumberPattern,
                                }),
                              }}
                              addition={{
                                label: 'Toevoeging',
                                placeholder: 'A',
                                ...register('houseNumberAddition', {
                                  pattern: houseNumberAdditionPattern,
                                }),
                              }}
                            />
                          </Stack>
                        </GridItem>
                        <GridItem columnStart="1" columnEnd="-1">
                          <Box
                            width={{ initial: '100%', md: 'auto' }}
                            paddingTop={{ initial: 2, md: 8 }}
                          >
                            <PostalCodeCheckForm.SubmitButton
                              size="large"
                              tone="onDark"
                              variant="secondary"
                              isLoading={fetchStatus === 'fetching'}
                              onClick={() =>
                                trackCustomWizard({
                                  action: 'submit',
                                  actionDetail: `${WIZARD_NAME}: bevestigen details`,
                                  wizardName: WIZARD_NAME,
                                  wizardStep: 2,
                                })
                              }
                            >
                              {props?.submitButtonLabel ?? 'Controleer uw postcode'}
                            </PostalCodeCheckForm.SubmitButton>
                          </Box>
                        </GridItem>
                      </Grid>
                    </PostalCodeCheckForm>
                  </Box>
                </CTABlock.Column>
                <CTAImageContainer
                  postalCode={postalCode}
                  congestionData={congestionData}
                  toggleMessage={toggleMessage}
                  handleToggleMessage={() => setToggleMessage(false)}
                />
              </CTABlock>
            </GridItem>
          </PageGrid>
        </Stack>
      </ScrollLinkWrapper>
      <Box ref={additionalInfoRef}>
        <Conclusion {...congestionData} />
      </Box>
    </Stack>
  );
};
